import { word } from "@modules/core/utils/i18n"
import { requiredInput } from "@modules/form/formUtils"
import { type SharedCoach } from "@modules/programs/types/programTypes"
import { type HUImage } from "@modules/types/imageTypes"
import * as Yup from "yup"
import * as z from "zod"

export type ProgramInfiniteInputKey =
  | "image"
  | "name"
  | "visibility"
  | "description"
  | "defaultHideSessionContent"
  | "entranceCheckEnabled"
  | "scoreboardLocked"
  | "sharedCoaches"

export function getProgramValidationSchema(options?: { imageRequired: boolean }) {
  return Yup.object({
    image: options?.imageRequired
      ? Yup.mixed().required(word("program.form.uploadImage.requiredText"))
      : Yup.object().nullable(),
    name: requiredInput(
      Yup.string()
        .max(255, word("form.maximum.characters", { count: 255 }))
        .trim(),
    ),
    description: requiredInput(Yup.string().trim()),
  })
}

export function getProgramPriceSchema(minAmount: number, maxAmount: number) {
  return z
    .object({
      value: z
        .number({ error: word("global.form.required") })
        .min(minAmount, word("global.form.minimumValue", { minValue: minAmount }))
        .max(maxAmount, word("global.form.maximumValue", { maxValue: maxAmount })),
      currency: z.string(),
      addFreeTrial: z.boolean(),
      trialDuration: z
        .number()
        .int()
        .min(1, word("program.duration.minimum.message"))
        .max(365, word("program.duration.maximum.message"))
        .nullable(),
    })
    .superRefine((data, ctx) => {
      if (data.addFreeTrial && data.trialDuration === null) {
        ctx.addIssue({
          code: "custom",
          path: ["trialDuration"],
          message: word("global.form.required"),
        })
      }
    })
}

export type ProgramFormikType = {
  image?: HUImage
  title: string
  description: string
  visibility: boolean
  defaultHideSessionContent: boolean
  scoreboardLocked: boolean
  entranceCheckEnabled: boolean
  sharedCoaches?: SharedCoach[]
}

export type ProgramPriceFormType = {
  value: number | undefined
  currency: string
  addFreeTrial: boolean
  trialDuration: number | null
}
